import type { CMSRoomType } from '@/cms/fields/mappers/productDetailPageFieldsMapper'
import { LazyImage } from '@/scopes/common/components/Image'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'

import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'

import { mqMin, tokPx } from '@sunwebgroup/ui-components'
import { useRef } from 'react'
import styled from 'styled-components'
import { Carousel, Container, useCarouselItemsInView } from '../Carousel'

interface GalleryProps {
  roomType: CMSRoomType
  handleDetailImageClick: (index: number) => void
}

const Wrapper = styled.div`
  ${Container} {
     overflow: visible;
  }
`
export const ImageElementButton = styled.div.attrs(() => ({
  role: 'button',
  tabIndex: -1,
}))`
  position: relative;
  display: block;
  border-radius: ${tokPx('primitives_corner_radius_m')};
  overflow: hidden;
  width: 100%;
  transition: all 0.3s ease-in-out;


  @media ${mqMin('primitives_breakpoint_m_min')} {
    border-radius: ${tokPx('primitives_corner_radius_m')};
  }

  &:hover {
    filter: brightness(0.85);
  }
`

const ImageElement = styled.div`
  position: relative;
  border-radius: ${tokPx('primitives_corner_radius_m')};
`
const LazyImageElementWrapper = styled.div`
  width: 100%;
  aspect-ratio: 3/2;
  position: relative;
  cursor: pointer;

`
const LazyImageElementWrapperInCarousel = styled.div`
  width: 360px;
  height: 240px;
  position: relative;
  cursor: grabbing;

  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: 390px;
    height: 260px;
  }
`

export function Gallery({ roomType, handleDetailImageClick }: GalleryProps) {
  const { sendEvent } = useAnalytics()
  const ref = useRef(null)
  const { isVisibleItem } = useCarouselItemsInView(ref)

  const handleImageClick = (index: number) => {
    handleDetailImageClick(index)
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: 'Roomtype images',
        result: `Open image ${index + 1}`,
        type: roomType.name,
      }),
    )
  }

  if (roomType.imageUrls.length === 1) {
    return (
      <Wrapper>
        <ImageElement>
          <ImageElementButton onClick={() => handleImageClick(0)}>
            <LazyImageElementWrapper>
              <LazyImage
                src={roomType.imageUrls[0]}
                srcSet={[480]} // srcset values are a bit larger compared to the sizes, because most images are served in 3/2 format, but shown in something like 4/3 format (depending on height of information on ProductCard)
              />
            </LazyImageElementWrapper>
          </ImageElementButton>
        </ImageElement>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Carousel>
        {roomType.imageUrls.map((mediaElement, index) => (
          <ImageElement key={`carousel-item-${index}`} ref={ref}>
            <ImageElementButton onClick={() => handleImageClick(index)}>
              <LazyImageElementWrapperInCarousel>
                <LazyImage
                  src={mediaElement}
                  srcSet={[480]} // srcset values are a bit larger compared to the sizes, because most images are served in 3/2 format, but shown in something like 4/3 format (depending on height of information on ProductCard)
                  loading={!isVisibleItem ? 'lazy' : 'eager'}
                />
              </LazyImageElementWrapperInCarousel>
            </ImageElementButton>
          </ImageElement>
        ))}
      </Carousel>
    </Wrapper>
  )
}
